import React, { useEffect, useMemo, useState } from "react";
import { AiOutlineCopy, AiOutlineDownload } from "react-icons/ai";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { uploadPfp, UploadUserTraits, useGetAllReferral } from "./hooks/helper";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAvatar } from "./hooks/AvatarContext";
import { useAppDispatch } from "../../controller/state/hooks";
import { useAccount } from "wagmi";
import { useAuth } from "./hooks/Authstate";
import { traitNames } from "./traitname";
import MyAvatar from "./MyAvatr";
import { useLocation } from "react-router-dom";

const CLAIM_TIMEOUT = 2 * 60 * 60;
const CLAIM_KEY = "ts-last-claim-time";
const TABS_KEY = "ts-avatar-tabs";
const CATEGORY_KEY = "ts-avatar-category";
const AVAILABLE_ITEMS_KEY = "ts-available-items";
const SAVED_AVATAR_KEY = "saved_avatar_layers";

export type Category = {
  label: string;
  svg: string;
  select: string;
  items: string[];
  availableItem?: string[];
  activeItem?: number;
};

export const CategoriesTab: Category[] = [
  {
    label: "Hat",
    svg: "/assets/main_v4/lootbox/avticons/hat.svg",
    select: "/assets/main_v4/lootbox/avticons/active_hat.svg",
    availableItem: [],
    items: [
      "Hat #1",
      "Hat #2",
      "Hat #3",
      "Hat #4",
      "Hat #5",
      "Hat #6",
      "Hat #7",
      "Hat #8",
      "Hat #9",
      "Hat #10",
      "Hat #11",
    ],
    activeItem: -1,
  },
  {
    label: "Eyes",
    svg: "/assets/main_v4/lootbox/avticons/eye.svg",
    select: "/assets/main_v4/lootbox/avticons/active_eye.svg",
    availableItem: [],
    items: [
      "Eyes #1",
      "Eyes #2",
      "Eyes #3",
      "Eyes #4",
      "Eyes #5",
      "Eyes #6",
      "Eyes #7",
      "Eyes #8",
    ],
    activeItem: -1,
  },
  {
    label: "Mouth",
    svg: "/assets/main_v4/lootbox/avticons/mouth.svg",
    select: "/assets/main_v4/lootbox/avticons/active_mouth.svg",
    availableItem: [],
    items: ["Mouth #1", "Mouth #2", "Mouth #3", "Mouth #4", "Mouth #5"],
    activeItem: -1,
  },
  {
    label: "Clothes",
    svg: "/assets/main_v4/lootbox/avticons/clothes.svg",
    select: "/assets/main_v4/lootbox/avticons/active_clothes.svg",
    availableItem: [],
    items: [
      "Clothes #1",
      "Clothes #2",
      "Clothes #3",
      "Clothes #4",
      "Clothes #5",
      "Clothes #6",
    ],
    activeItem: -1,
  },
  {
    label: "Body",
    svg: "/assets/main_v4/lootbox/avticons/body.svg",
    select: "/assets/main_v4/lootbox/avticons/active_body.svg",
    availableItem: ["Body #1"],
    items: ["Body #1"],
    activeItem: 0,
  },
  {
    label: "Bg",
    svg: "/assets/main_v4/lootbox/avticons/background.svg",
    select: "/assets/main_v4/lootbox/avticons/active_background.svg",
    availableItem: [],
    items: ["Background #1", "Background #2", "Background #3", "Background #4"],
    activeItem: -1,
  },
];

const randomPools: Record<string, string[]> = {
  eyes: ["Eyes #4"],
  mouth: ["Mouth #4"],
  hat: ["Hat #4"],
  clothes: ["Clothes #4"],
  bg: ["Background #4"],
  body: ["Body #1"],
};

const randomPool: Record<string, string[]> = {
  eyes: ["Eyes #1", "Eyes #2"],
  mouth: ["Mouth #1", "Mouth #2"],
  hat: ["Hat #1", "Hat #2"],
  clothes: ["Clothes #1", "Clothes #2"],
  bg: ["Background #1", "Background #2"],
  body: ["Body #1"],
};

export const getRandomizedTabs = (): Category[] => {
  const newTabs = JSON.parse(JSON.stringify(CategoriesTab)) as Category[];
  newTabs.forEach((category) => {
    const key = category.label.toLowerCase();
    const pool = randomPool[key] || category.items;
    const randIndex = Math.floor(Math.random() * pool.length);
    const selected = pool[randIndex];
    category.availableItem = [selected];
    const itemIndex = category.items.indexOf(selected);
    category.activeItem =
      category.label === "Body" ? 0 : Math.random() > 0.5 ? itemIndex : -1;
  });
  return newTabs;
};

export const getRandomizedTraits = (): Category[] => {
  const newTabs = JSON.parse(JSON.stringify(CategoriesTab)) as Category[];

  // Select a random category
  const categoryKeys = Object.keys(randomPools);
  const randomCategoryKey =
    categoryKeys[Math.floor(Math.random() * categoryKeys.length)];
  const randomCategoryLabel =
    randomCategoryKey.charAt(0).toUpperCase() + randomCategoryKey.slice(1);

  // Find the corresponding category in the tabs
  const categoryToRandomize = newTabs.find(
    (cat) => cat.label.toLowerCase() === randomCategoryKey
  );

  if (categoryToRandomize) {
    const pool = randomPools[randomCategoryKey] || categoryToRandomize.items;
    const randIndex = Math.floor(Math.random() * pool.length);
    const selected = pool[randIndex];
    // const [newFlags, setNewFlags] = useState<Record<string, boolean>>({});
    categoryToRandomize.availableItem = [selected];
    const itemIndex = categoryToRandomize.items.indexOf(selected);
    categoryToRandomize.activeItem =
      categoryToRandomize.label === "Body"
        ? 0
        : Math.random() > 0.5
        ? itemIndex
        : -1;
  }

  return newTabs;
};

const TownesquareMainAvatar: React.FC = () => {
  // const [tabs, setTabs] = useState<Category[]>(() => {
  //   const savedTabs = localStorage.getItem(TABS_KEY);
  //   return savedTabs ? JSON.parse(savedTabs) : getRandomizedTabs();
  // });
  const [tabs, setTabs] = useState<Category[]>(CategoriesTab);
  const [selectedCategory, setSelectedCategory] = useState("Hat");
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(true);
  const account = useAccount();
  const [savedLayers, setSavedLayers] = useState<(string | null)[] | null>(
    null
  );
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const { setAvatar } = useAvatar();
  const { data, refetch } = useGetAllReferral();
  const location = useLocation();
  const [newFlags, setNewFlags] = useState(location.state?.newFlags || {});
  const userdetails = data?.user ?? null;

  useEffect(() => {
    if (account?.isConnected && token) {
      queryClient.invalidateQueries({
        queryKey: ["userReferral"],
        refetchType: "active",
      });
    }
  }, [account?.isConnected, token, queryClient]);

  useEffect(() => {
    if (!userdetails?.traits) return;

    let userTraits: { label: string; availableItem: string }[] = [];

    if (typeof userdetails.traits === "string") {
      try {
        userTraits = JSON.parse(userdetails.traits);
      } catch (error) {
        console.error("Failed to parse user traits", error);
        return;
      }
    } else if (Array.isArray(userdetails.traits)) {
      userTraits = userdetails.traits;
    }

    const savedActiveItems = JSON.parse(
      localStorage.getItem("activeItems") || "{}"
    );

    const updatedTabs = CategoriesTab.map((tab, i) => {
      const backendTrait = userTraits.find(
        (userTrait) => userTrait.label.toLowerCase() === tab.label.toLowerCase()
      );

      if (backendTrait?.availableItem) {
        const availableItems = Array.isArray(backendTrait.availableItem)
          ? Array.from(new Set(backendTrait.availableItem))
          : [backendTrait.availableItem];

        const savedIndex = savedActiveItems[i];
        const fallbackIndex = tab.items.findIndex(
          (item) => item === availableItems[0]
        );

        return {
          ...tab,
          availableItem: availableItems,
          activeItem: savedIndex ?? (fallbackIndex !== -1 ? fallbackIndex : -1),
        };
      }

      return tab;
    });

    setTabs(updatedTabs);
  }, [userdetails]);

  const handleItemClick = (tabIndex: number, itemIndex: number) => {
    const newTabs = [...tabs];
    newTabs[tabIndex].activeItem = itemIndex;
    setTabs(newTabs);

    // Save selected index to localStorage
    const saved = JSON.parse(localStorage.getItem("activeItems") || "{}");
    saved[tabIndex] = itemIndex;
    localStorage.setItem("activeItems", JSON.stringify(saved));
  };

  const getLayerImage = (label: string, index: number) =>
    index > -1
      ? `/assets/main_v4/lootbox/compose/${label.toLowerCase()}/${label.toLowerCase()}-${
          index + 1
        }.png`
      : null;

  const layers = [
    getLayerImage("bg", tabs[5]?.activeItem ?? -1),
    getLayerImage("body", tabs[4]?.activeItem ?? -1),
    getLayerImage("clothes", tabs[3]?.activeItem ?? -1),
    getLayerImage("mouth", tabs[2]?.activeItem ?? -1),
    getLayerImage("eyes", tabs[1]?.activeItem ?? -1),
    getLayerImage("hat", tabs[0]?.activeItem ?? -1),
  ];

  const currentTab = tabs.find((t) => t.label === selectedCategory);
  const currentTabIndex = tabs.findIndex((t) => t.label === selectedCategory);

  useEffect(() => {
    if (userdetails?.pfp) {
      setSavedLayers([...layers]);
    }
  }, [userdetails?.pfp]);

  useEffect(() => {
    const currentLayers = layers.filter((layer) => layer !== null) as string[];
    const savedLayers = JSON.parse(
      localStorage.getItem(SAVED_AVATAR_KEY) || "[]"
    );
    setHasChanges(
      currentLayers.length !== savedLayers.length ||
        !currentLayers.every((layer, i) => layer === savedLayers[i])
    );
  }, [layers]);

  const handleSaveAvatarPfp = async () => {
    // Check for changes
    const currentLayers = layers.filter((layer) => layer !== null) as string[];
    const savedLayers = JSON.parse(
      localStorage.getItem(SAVED_AVATAR_KEY) || "[]"
    );

    if (
      currentLayers.length === savedLayers.length &&
      currentLayers.every((layer, i) => layer === savedLayers[i])
    ) {
      toast.error("No changes detected in avatar traits");
      return;
    }

    setIsSaving(true);
    try {
      // Create canvas with sequential layer loading (fixes missing layers)
      const canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext("2d");

      // Load layers IN ORDER (not Promise.all)
      for (const layer of layers) {
        if (!layer) continue;
        await new Promise<void>((resolve) => {
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.src = layer;
          img.onload = () => {
            ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve();
          };
          img.onerror = () => resolve();
        });
      }

      // Convert to blob with high quality
      const blob = await new Promise<Blob | null>(
        (resolve) => canvas.toBlob(resolve, "image/png", 1.0) // Highest quality
      );
      if (!blob) throw new Error("Canvas conversion failed");

      // Upload to server
      const file = new File([blob], "avatar.png", { type: "image/png" });
      const formData = new FormData();
      formData.append("avatar", file);

      const token = localStorage.getItem("token");
      if (!token) throw new Error("Authentication required");

      const uploadResponse = await fetch(
        "https://action-link-stage.townesquare.xyz/api/users/kuru-resources-avatar",
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      if (!uploadResponse.ok) throw new Error("Upload failed");
      const responseData = await uploadResponse.json();
      console.log("Upload response:", responseData);

      // Update all states
      localStorage.setItem(SAVED_AVATAR_KEY, JSON.stringify(currentLayers));
      uploadPfp(responseData.data?.avatar);
      setAvatar(`${responseData.data?.avatar}?v=${Date.now()}`);
      await refetch();
      setHasChanges(false);
      // Invalidate queries
      await Promise.all([
        refetch(),
        queryClient.invalidateQueries({ queryKey: ["userData"] }),
      ]);

      setTimeout(() => {
        toast.success("Avatar updated with all layers!", {
          position: "top-right",
          autoClose: 3000,
          theme: "dark",
        });
      }, 3000);
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("Failed to update avatar. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  //download avatar
  const handleDownload = async () => {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext("2d");

    for (const layer of layers) {
      if (layer) {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = layer;
        await new Promise((res) => {
          img.onload = () => {
            ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
            res(true);
          };
        });
      }
    }

    const link = document.createElement("a");
    link.download = "townesquare-avatar.png";
    link.href = canvas.toDataURL();
    link.click();

    toast.success("Avatar downloaded!", {
      position: "top-right",
      autoClose: 3000,
      theme: "dark",
    });
  };

  //copy Avatar
  const handleCopyAvatarImage = async () => {
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext("2d");

    for (const layer of layers) {
      if (layer) {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = layer;
        await new Promise((res) => {
          img.onload = () => {
            ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
            res(true);
          };
        });
      }
    }

    canvas.toBlob(async (blob) => {
      if (blob) {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({ "image/png": blob }),
          ]);
          toast.success("Avatar copied to clipboard!", {
            position: "top-right",
            autoClose: 3000,
            theme: "dark",
          });
        } catch (err) {
          console.error("Copy failed:", err);
          toast.error("Failed to copy avatar. Try again.", {
            position: "top-right",
            autoClose: 3000,
            theme: "dark",
          });
        }
      }
    }, "image/png");
  };

  return (
    <div className="flex flex-col justify-center items-center mx-[116px] ">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
      <div className="flex flex-row justify-center mt-10 gap-16">
        {/* LEFT PANEL */}
        <div className="space-y-2 w-[284px] bg-black text-white p-4 rounded-md">
          {tabs.map((tab) => (
            <div
              key={tab.label}
              onClick={() => setSelectedCategory(tab.label)}
              className={`flex items-center justify-between px-4 py-2 h-[52px] w-full rounded-[8px] cursor-pointer border ${
                selectedCategory === tab.label
                  ? "border-[#B882FF] bg-[#1c1c1c]"
                  : "border-transparent"
              }`}
            >
              <div className="flex items-center space-x-4">
                <div className="flex justify-center items-center ">
                  <img
                    src={selectedCategory === tab.label ? tab.select : tab.svg}
                    alt="tab icon"
                  />
                </div>
                <span>{tab.label === "Bg" ? "Background" : tab.label}</span>
                {newFlags[tab.label] && tab.label !== "Body" && (
                  <span className="ml-2 bg-[#2AB57626] text-[#2AB576] text-[12px] font-semibold px-2 py-[2px] rounded-[3px]">
                    NEW
                  </span>
                )}
              </div>
              {selectedCategory === tab.label && (
                <img src="/assets/main_v4/lootbox/composeAvatar/Check.svg" />
              )}
            </div>
          ))}
        </div>

        {/* PREVIEW PANEL */}
        <div className="p-2 flex flex-col items-center">
          <div className="relative w-[285px] h-[301px] border rounded-[8px] mb-5 bg-[#121212] overflow-hidden">
            {layers.map(
              (layer, index) =>
                layer && (
                  <img
                    key={index}
                    src={layer}
                    alt={`layer-${index}`}
                    className="absolute top-0 left-0 w-full h-full object-contain"
                    style={{ zIndex: index + 1 }}
                  />
                )
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSaveAvatarPfp}
              disabled={isSaving || !hasChanges}
              className={`w-[150px] h-[36px] text-[15px] rounded-[32px] text-white ${
                isSaving || !hasChanges
                  ? "bg-[#9264F8]/50 cursor-not-allowed"
                  : "bg-[#9264F8]"
              }`}
            >
              {isSaving ? "Saving..." : "Set as PFP"}
            </button>

            <button
              onClick={handleDownload}
              className="bg-white text-[#121212] font-meduim text-[15px] w-[150px] h-[36px] rounded-[32px] flex items-center justify-center gap-2"
            >
              <img src="/assets/main_v4/ddownload.svg" className="w-5 h-5" />
              Download
            </button>

            <button
              className="bg-white text-[#121212] font-meduim text-[15px] w-[150px] h-[36px] rounded-[32px] flex items-center justify-center gap-2"
              onClick={handleCopyAvatarImage}
            >
              <img src="/assets/main_v4/dcopy.svg" className="w-5 h-5" />
              Copy
            </button>
          </div>
        </div>
      </div>

      {/* TRAITS PANEL */}
      {currentTab && (
        <div className="flex flex-col justify-center mt-6 w-[926px]">
          <span className="text-[20px] font-semibold py-4">
            My {selectedCategory === "Bg" ? "Background" : selectedCategory}{" "}
            Traits
          </span>

          <div className="flex flex-wrap justify-start gap-4 w-full">
            <div
              onClick={() => handleItemClick(currentTabIndex, -1)}
              className={`relative h-[200px] w-[166px] p-1 cursor-pointer rounded-xl bg-[#222222] flex flex-col items-center justify-center ${
                currentTab.activeItem === -1 ? "ring-2 ring-[#B882FF]" : ""
              }`}
            >
              <img src="/assets/main_v4/Prohibit.png" />
              <p className="text-white text-center mt-1 text-sm">None</p>
              {currentTab.activeItem === -1 && (
                <div className="absolute top-2 right-2 text-white p-1 rounded-full">
                  <img src="/assets/main_v4/Check.png" className="w-5 h-5" />
                </div>
              )}
            </div>

            {currentTab.availableItem?.map((item) => {
              const realIndex = currentTab.items.findIndex((i) => i === item);

              return (
                <div
                  key={realIndex}
                  onClick={() => handleItemClick(currentTabIndex, realIndex)}
                  className={`relative h-[200px] w-[166px] p-1 cursor-pointer rounded-xl bg-[#222222] ${
                    currentTab.activeItem === realIndex
                      ? "ring-4 ring-[#B882FF]"
                      : ""
                  }`}
                >
                  <img
                    src={`/assets/main_v4/lootbox/compose/${currentTab.label.toLowerCase()}/${currentTab.label.toLowerCase()}-${
                      realIndex + 1
                    }.png`}
                    className="w-full h-[135px] object-contain"
                    alt={item}
                  />
                  <p className="text-white text-center text-sm">
                    {traitNames[item]?.name || item}
                  </p>

                  {traitNames[item]?.tag &&
                    (() => {
                      let tagColor = "#AAAAAA";
                      if (traitNames[item].tag === "Rare") tagColor = "#257AFA";
                      else if (traitNames[item].tag === "Epic")
                        tagColor = "#00EEFD";

                      return (
                        <p
                          className="w-[58px] h-[21px] mb-2 rounded-full flex items-center justify-center mt-2 text-[11px] leading-tight font-medium tracking-wide mx-auto"
                          style={{
                            color: tagColor,
                            border: `1px solid ${tagColor}`,
                          }}
                        >
                          {traitNames[item].tag}
                        </p>
                      );
                    })()}

                  {currentTab.activeItem === realIndex && (
                    <div className="absolute top-2 right-2 text-white p-1 rounded-full">
                      <img
                        src="/assets/main_v4/Check.png"
                        className="w-5 h-5"
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {/* <MyAvatar/> */}
    </div>
  );
};

export default TownesquareMainAvatar;
